import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';

import * as fromStore from '../../../store';
import { HeaderService } from 'src/app/core/services/header';
import { Comment } from 'src/app/models/comment.model';
import { BusinessPurpose, Ticket } from 'src/app/models/ticket/ticket.model';
import { Permission } from 'src/app/shared/constants/permissions';
import { CommentService } from '../../services';

@Component({
  selector: 'app-ticket-comment',
  templateUrl: './ticket-comment.component.html',
  styleUrls: ['./ticket-comment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketCommentComponent implements OnInit, OnDestroy, OnChanges {
  @Input() ticket: Ticket;

  @Input() viewMoreComments: boolean;

  @Input() widgetHeight: number;

  @Input() descriptionContentHeight: number;

  @Input() fileUploadResult: any;

  @Output() reloadTicketsData: EventEmitter<any> = new EventEmitter();

  @Output() saved = new EventEmitter<void>();

  @ViewChild('autosize', { static: false }) autosize: CdkTextareaAutosize;

  @ViewChild('commentsContainer', { static: false })
  commentsContainer: ElementRef;

  commentForm: UntypedFormGroup;

  comments: Comment[];

  comment: Comment;

  isCommentSubmitting = false;

  isCommentEmpty = true;

  hasError = false;

  isTicketIdOnRouter = false;

  ticket$: Observable<Ticket>;

  ticketId: string;

  apiEmployeeId: string;

  browserSize$: Observable<string> = this.store.select(
    fromStore.getBrowserSizeName,
  );

  browserSize: string;

  heightDiff = 0;

  commentHeight = 200;

  Permission = Permission;

  BusinessPurpose = BusinessPurpose;

  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private commentService: CommentService,
    private router: Router,
    private fb: UntypedFormBuilder,
    public header: HeaderService,
    private store: Store<fromStore.State>,
  ) {}

  ngOnInit(): void {
    this.browserSize$.pipe(takeUntil(this.destroy$)).subscribe((name) => {
      if (!name) return;

      const isResizingFromSmall =
        this.browserSize === 'small' && name !== this.browserSize;

      if (isResizingFromSmall) {
        this.router.navigateByUrl('/tickets');
      }

      this.browserSize = name;
    });

    this.heightDiff = this.widgetHeight - this.descriptionContentHeight;

    this.initialize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initialize();
    this.fileUploadResultChange(changes);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fileUploadResultChange(changes: SimpleChanges) {
    const oldName: string =
      changes.fileUploadResult?.previousValue?.files?.[0]?.fileName ?? '';
    const newName: string =
      changes.fileUploadResult?.currentValue.files?.[0].fileName ?? '';
    const success: boolean =
      changes.fileUploadResult?.currentValue.files?.[0].success ?? false;

    if (newName !== oldName && success) {
      this.saveComment({
        Case__c: this.ticket.Id,
        Body__c: `File '${newName}' added.`,
      });
    }
  }

  initialize(): void {
    this.isTicketIdOnRouter = !!this.route.snapshot.paramMap.get('ticketId');
    if (this.browserSize === 'small') {
      this.onSmallScreen();
    } else {
      this.onLargeScreen();
    }

    this.fileUploadResult = {};
  }

  onSmallScreen(): void {
    this.ticket$ = this.store.select(fromStore.getCurrentRouterTicket);
    this.ticket$.pipe(takeUntil(this.destroy$)).subscribe((ticket) => {
      if (!ticket?.Id) return;
      this.ticket = ticket;
      this.populateCommentForm();
      this.scrollToBottom();
      this.cdr.detectChanges();
    });

    setTimeout(() => {
      this.markCommentsAsRead();
    }, 5000);
  }

  onLargeScreen(): void {
    if (this.isTicketIdOnRouter) {
      this.router.navigateByUrl('/tickets');
    }

    this.populateCommentForm();
    this.getCommentHeight();
  }

  populateCommentForm(): void {
    this.commentForm = this.fb.group({
      Case__c: this.ticket ? [`${this.ticket.Id}`] : [''],
      Body__c: ['', [Validators.required]],
    });
  }

  markCommentsAsRead(): void {
    // if ticket has unread comments, change them to read
    if (this.ticket?.Id) {
      if (this.ticket?.Comments__r.totalSize > 0) {
        if (this.hasUnreadComments(this.ticket.Comments__r.records)) {
          const updatePayload = {
            Case__c: this.ticket.Id,
            Is_Read__c: true,
          };
          this.commentService
            .markCommentsAsRead(updatePayload)
            .subscribe(() => {
              this.store.dispatch(new fromStore.ClearTickets());
              this.store.dispatch(new fromStore.LoadTickets());
              this.onSmallScreen();
            });
        }
      }
    }
  }

  hasUnreadComments = (comments: Comment[]): boolean => {
    if (comments.length) {
      return comments.some((c) => !c.Is_Read__c);
    }
    return false;
  };

  scrollToBottom(): void {
    if (this.commentsContainer) {
      this.commentsContainer.nativeElement.scrollTop =
        this.commentsContainer.nativeElement.scrollHeight;
      window.scrollTo(0, this.commentsContainer.nativeElement.scrollHeight);
    }
  }

  getCommentHeight(): void {
    if (this.widgetHeight - this.descriptionContentHeight - 70 > 200) {
      this.commentHeight =
        this.widgetHeight - this.descriptionContentHeight - 70;
    }
  }

  saveFormComment(form: UntypedFormGroup): void {
    if (!this.isCommentEmpty) {
      if (this.commentForm.valid) {
        this.saveComment(form.value);
      }
    }
  }

  private saveComment(comment: any) {
    this.isCommentSubmitting = true;

    this.commentService.createComment(comment).subscribe(
      (data) => {
        // Reset the form to clear the flags
        this.isCommentSubmitting = false;
        if (data?.isSuccess) {
          this.isCommentEmpty = true;
          // this.commentForm.reset();
          this.commentForm.reset({ Case__c: this.ticket.Id, Body__c: '' });
        }

        // reload tickets & comments
        this.store.dispatch(new fromStore.LoadTickets());
        if (this.browserSize === 'small') {
          this.onSmallScreen();
        }

        this.saved.emit();
        this.cdr.detectChanges();
      },
      () => {
        this.isCommentSubmitting = false;
        this.hasError = true;
        this.cdr.detectChanges();
      },
    );
  }

  onCommentChanges($event: any): void {
    if (this.browserSize !== 'small') {
      // remove spaces to count only letters.
      const text = $event.replace(/\s+/g, '');
      if (text.length > 1) {
        this.isCommentEmpty = false;
      } else {
        this.isCommentEmpty = true;
      }
    }
  }
}
